import { type DataFunctionArgs, json } from '@remix-run/node'
import { useLoaderData, useNavigate } from '@remix-run/react'
import { type RoleType } from 'types/enums.ts'
import { MainContainer } from '~/components/layout/main-container.tsx'
import { MainLayout } from '~/components/layout/main-layout.tsx'
import { Card } from '~/components/ui/card.tsx'
import { Icon } from '~/components/ui/icons/icon.tsx'
import { LegalPage, Title } from '~/components/ui/legalPage.tsx'
import { Spacer } from '~/components/ui/spacer.tsx'
import { getUserRole } from '~/features/auth/auth.server.ts'

export async function loader({ request }: DataFunctionArgs) {
  const userRole = await getUserRole(request)
  return json({ userRole })
}

export default function Terms() {
  const { userRole } = useLoaderData<typeof loader>()
  const navigate = useNavigate()
  return (
    <MainLayout userRole={userRole as RoleType | undefined}>
      <MainContainer>
        <div
          onClick={() => navigate(-1)}
          className="flex cursor-pointer items-center gap-2 text-foreground-muted"
        >
          <Icon name="arrow-up" className="-rotate-90" />
          <span>Volver</span>
        </div>
        <Spacer size="4xs" />
        <p className="text-h2 text-foreground-muted">AVISO LEGAL</p>
        <Spacer size="4xs" />
        <div className="flex flex-col gap-4 lg:flex-row lg:gap-10">
          <Card className="h-full p-6 md:p-10">
            <LegalPage>
              <Title
                title="1. TE DAMOS LA BIENVENIDA A HTTPS://WWW.MIISTICO.COM"
                spacer={false}
              />

              {`
En MIISTICO te ofrecemos el sitio web https://www.miistico.com, y en su caso, las aplicaciones móviles y otras propiedades interactivas, que MIISTICO posee y opera (conjuntamente, la "Web").

El acceso a la Web te confiere la condición de “Usuario”, entendiendo como Usuario aquél que utiliza la Web con la finalidad de obtener información acerca de los productos y servicios que se ofertan. En aplicación y cumplimiento del deber de información dispuesto en el artículo 10 de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la Información y de Comercio Electrónico se facilita la siguiente información acerca de la Web.
`}

              <Title title="1.1 Conócenos" isSubtitle={true} />
              <div className="mt-5 grid gap-3">
                <p>
                  Nombre o Denominación Social del titular: MIISTICO MARKET,
                  S.L.
                </p>
                <p>NIF: B42798348</p>
                <p>
                  Domicilio social del titular: Carrencà nº23, 1º, 08017
                  Barcelona
                </p>
                <p>Teléfono: +34 647449788</p>
                <p>E-mail de contacto: info@miistico.com</p>
                <p>Sitio web: https://www.miistico.com</p>
                <p>
                  MIISTICO MARKET S.L. ejerce su actividad empresarial prestando
                  un servicio de SERVICIOS MISTICOS (Tarot, Astrología,
                  Numerología, Reiki...).
                </p>
                <p>
                  Además,MIISTICO MARKET S.L. se encuentra adherido al código de
                  conducta{' '}
                  <a href="https://miistico.com/condiciones-uso">
                    https://miistico.com/condiciones-uso
                  </a>
                  .
                </p>
              </div>

              <Title title="2. USO DE LA WEB" />
              <Title
                title="2.1 Acceso a la Web"
                isSubtitle={true}
                spacer={false}
              />
              {`
Este Aviso Legal regula el acceso y uso de https://www.miistico.com que MIISTICO pone a tu disposición. La continuación del uso y navegación en la Web conlleva la aceptación de este Aviso Legal.

Los servicios prestados por MIISTICO a través de la Web se encuentran regulados por los/las siguientes Términos y Condiciones de Uso.

La Web es de acceso libre y gratuito, y con carácter general, no exige la previa suscripción o registro en la Web, sin embargo, puede que algunas secciones de la misma requieran que reviamente rellenes un formulario.

Como Usuario, te comprometes a realizar un uso de la Web, de sus contenidos y de sus servicios, acorde con este Aviso Legal, la legislación aplicable, el orden público y las buenas costumbres y a no hacer un uso dirigido a dañar, inutilizar y/o sobrecargar la Web, o que pueda impedir, de cualquier forma, la normal utilización y funcionamiento de la misma.

En MIISTICO realizamos un esfuerzo continuado para mantener la Web en buen funcionamiento, manteniendo los contenidos actualizados. No obstante, en MIISTICO no podemos garantizar la completa disponibilidad y continuidad del acceso a la Web, o de la actualización de sus contenidos.

Si tienes alguna duda relacionada con la Política de Privacidad y Cookies o el Aviso Legal puedes ponerte en contacto con nosotros a través de nuestros canales habituales de contacto y si no estás de acuerdo con ellos, te pedimos que salgas de esta página y no realices ninguna acción.
`}

              <Title
                title="2.2 Limitaciones de uso de la Web"
                isSubtitle={true}
              />
              {`
Al hacer uso de la Web te comprometes a no:

Realizar actuaciones susceptibles de alterar, deteriorar, interrumpir o dañar nuestros sistemas electrónicos o los de terceros; así como a no obstaculizar el acceso de otros usuarios a la Web y sus servicios mediante el consumo masivo de los recursos informáticos a través de los que prestamos nuestros servicios.

Intentar acceder a las cuentas de correo electrónico de otros usuarios o a áreas restringidas de nuestros sistemas informáticos o de los de terceros y, en su caso, extraer información.

Vulnerar nuestros derechos de propiedad intelectual o industrial o los derechos de terceros.

Reproducir, copiar, distribuir (o cualquier otra forma de comunicación pública), transformar o modificar los contenidos publicados en la Web.

Usar cualquier robot, araña u otro dispositivo, proceso o medio automático con cualquier finalidad, incluidas la supervisión o la copia de cualquier material de la Web.

Saltarse cualquier medida que podamos tomar para evitar o restringir el acceso a ciertas áreas de la Web.

Intentar de cualquier otra manera interferir en el funcionamiento correcto de la Web.

Si no cumples con lo dispuesto en este Aviso Legal o, cualesquiera otras condiciones recogidas en esta Web, nos reservamos el derecho a limitar, suspender y/o finalizar tu acceso a la misma, pudiendo adoptar para ello cualquier medida que sea necesaria.
`}

              <Title title="3. PROPIEDAD INTELECTUAL Y/O INDUSTRIAL" />
              {`
La Web, los contenidos y funcionalidades relacionadas con los mismos, el diseño de https://www.miistico.com así como las bases de datos, el software, los distintos elementos que integran el sitio web (textos, gráficos, fotografías, videos, grabaciones sonoras, combinaciones de colores, disposición y la selección, etc.), y signos distintivos (logotipos, marcas registradas y nombres comerciales) que aparecen en la Web son titularidad de MIISTICO MARKET S.L. o de los titulares de las respectivas licencias y se encuentran protegidos por los correspondientes derechos de propiedad intelectual e industrial.

En ningún caso, el acceso a la Web supone la cesión de ningún derecho de propiedad intelectual y/o industrial sobre la misma o sus contenidos.
`}

              <Title title="4. RESPONSABILIDAD" />
              <Title
                title="4.1 Sobre la información."
                isSubtitle={true}
                spacer={false}
              />
              {`
El contenido de la Web es de carácter general y tiene una finalidad informativa sin que garanticemos su exhaustividad, corrección, vigencia o utilidad para un fin concreto.
`}

              <Title
                title="4.2 Sobre los contenidos y servicios de terceros enlazados."
                isSubtitle={true}
              />
              {`
En ningún caso la existencia de sitios enlazados, integraciones, de la presencia de plugins o de mecanismos de búsqueda supone la recomendación o promoción de los sitios enlazados o sus contenidos por parte de MIISTICO. Es por ello que no nos hacemos responsables del contenido, manifestaciones o servicios prestados por los sitios web de terceros.

Te ofrecemos dichos enlaces e información y/o servicios porque entendemos que pueden ser de tu interés, siendo tu responsabilidad leer y aceptar los Términos de Uso y Políticas de Privacidad publicados en dichos sitios web o aplicaciones vinculadas.

En todo caso, procederemos a la retirada inmediata de cualquier enlace cuando los contenidos hacia los que redirige puedan contravenir la legislación nacional o internacional, la moral o el orden público. Es por ello que en el supuesto de que consideres que existe una web enlazada con contenidos ilícitos o inadecuados te pedimos que lo pongas en nuestro conocimiento para que podamos tomar las medidas que consideremos oportunas.
`}

              <Title
                title="4.3 De la disponibilidad de la web."
                isSubtitle={true}
              />
              {`
Hemos adoptado, y seguiremos adoptando cuantas medidas sean necesarias, dentro de nuestras posibilidades y las del estado de la tecnología, para garantizar el correcto funcionamiento de la Web y evitar la existencia de componentes dañinos. Sin embargo, no responderemos por los daños y perjuicios que te pudiera ocasionar a ti o a terceros la imposibilidad de acceder a la Web, así como de la presencia de virus u otros elementos dañinos.
`}

              <Title title="5. DISPOSICIONES VARIAS" />
              <Title
                title="5.1 Atención al cliente"
                isSubtitle={true}
                spacer={false}
              />

              {`
Para cualquier duda, sugerencia, consulta o reclamación relacionada con el uso de la Web ponte en contacto con nosotros a través del correo electrónico info@miistico.com y atenderemos tu consulta a la mayor brevedad posible, y en cualquier caso, antes de 30 días.
`}

              <Title
                title="5.2 Independencia e integridad de las cláusulas"
                isSubtitle={true}
              />
              {`
La ilicitud, invalidez o ineficacia de cualquiera de las cláusulas que conforman el presente Aviso Legal no afectará a la eficacia del resto, siempre que los derechos y obligaciones no se vean afectados de forma esencial. Dichas cláusulas deberán reemplazarse o integrarse con otras que, siendo conformes a la ley, respondan a la finalidad de las sustituidas.
`}

              <Title
                title="5.3 Modificación del Aviso Legal"
                isSubtitle={true}
              />
              {`
La fecha de última actualización de este Aviso Legal, aparece al pie de ésta página. Podremos revisar y actualizar este Aviso Legal en cualquier momento y te avisaremos de ello por los medios habituales, pero siempre que afecten a elementos materiales no entrarán en vigor hasta catorce (14) días después de publicarse.
`}

              <Title
                title="5.4 Jurisdicción y ley aplicable"
                isSubtitle={true}
              />
              {`
El acceso y uso de https://www.miistico.com, se regirán por lo dispuesto en la legislación española. Cualquier disputa o controversia entre el usuario y MIISTICO será resuelta por los juzgados y tribunales de la ciudad de Barcelona (España) con renuncia expresa a cualquier otro fuero que les corresponda.
`}
            </LegalPage>
          </Card>
        </div>
      </MainContainer>
    </MainLayout>
  )
}
